(() => {
  'use strict';

  if (!customElements.get('wi-reel-shop')) {
    customElements.define('wi-reel-shop', class WiReelShop extends HTMLElement {
      connectedCallback() {
        if (this.dataset.wirsInitialized === 'true') return;
        this.dataset.wirsInitialized = 'true';

        const root = this;
        const routeRoot = () => {
          const path = window.Shopify?.routes?.root || '/';
          return path.endsWith('/') ? path : `${path}/`;
        };

        const cloneTemplate = (template) => {
          if (!template?.content) return null;
          return template.content.cloneNode(true);
        };

        const rail = root.querySelector('[data-wirs-rail]');
        const lightbox = root.querySelector('[data-wirs-lightbox]');
        const viewer = root.querySelector('[data-wirs-viewer]');
        const lightboxVideo = root.querySelector('[data-wirs-lightbox-video]');
        const productSlot = root.querySelector('[data-wirs-product-slot]');
        const muteButton = root.querySelector('[data-wirs-mute]');
        const closeButton = root.querySelector('[data-wirs-close]');
        const prevButton = root.querySelector('[data-wirs-prev]');
        const nextButton = root.querySelector('[data-wirs-next]');
        const peekPrev = root.querySelector('[data-wirs-peek="prev"]');
        const peekNext = root.querySelector('[data-wirs-peek="next"]');
        const peekPrevImage = root.querySelector('[data-wirs-peek-image="prev"]');
        const peekNextImage = root.querySelector('[data-wirs-peek-image="next"]');
        const variantSheet = root.querySelector('[data-wirs-variant-sheet]');
        const variantSlot = root.querySelector('[data-wirs-variant-slot]');
        const variantBackdrop = root.querySelector('[data-wirs-variant-backdrop]');
        const variantClose = root.querySelector('[data-wirs-variant-close]');
        const toast = root.querySelector('[data-wirs-toast]');
        const toastName = root.querySelector('[data-wirs-toast-name]');

        if (!lightbox || !viewer || !lightboxVideo || !productSlot || !muteButton || !closeButton || !variantSheet || !variantSlot || !variantBackdrop) {
          root.dataset.wirsInitialized = 'false';
          return;
        }

        const cards = Array.from(root.querySelectorAll('[data-wirs-card]'));
        const gridVideos = Array.from(root.querySelectorAll('[data-wirs-grid-video]'));
        const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
        const mobileQuery = window.matchMedia?.('(max-width: 749px)') || null;
        const stageQuery = window.matchMedia?.('(min-width: 900px)') || null;
        const baseRoute = routeRoot();
        const cartAddUrl = `${baseRoute}cart/add.js`;

        let currentIndex = 0;
        let lastFocus = null;
        let previousHtmlOverflow = '';
        let previousBodyOverflow = '';
        let reduceMotion = Boolean(mediaQuery?.matches);
        let observer = null;
        let variantController = null;
        let cartRefreshController = null;
        let cartRefreshSequence = 0;
        let toastTimer = 0;
        let isAdding = false;
        let pointerStartX = 0;
        let pointerStartY = 0;
        let destroyed = false;
        const variantCache = new Map();

        const isMobile = () => Boolean(mobileQuery?.matches);

        const getCard = (index) => cards[index] || null;

        const getTemplate = (type, index) =>
          root.querySelector(`[data-wirs-${type}-template="${index}"]`);

        const ensureGridVideoReady = (video) => {
          if (!video) return;

          if (!video.poster && video.dataset.poster) {
            video.poster = video.dataset.poster;
          }

          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.load();
          }
        };

        const pauseGridVideos = () => {
          gridVideos.forEach((video) => video.pause());
        };

        const syncGridPlayback = () => {
          if (reduceMotion || document.hidden || lightbox.classList.contains('is-open')) {
            pauseGridVideos();
            return;
          }

          const limit = isMobile() ? 1 : 2;
          const candidates = gridVideos
            .filter((video) => Number(video.dataset.wirsRatio || 0) > 0)
            .sort((a, b) => Number(b.dataset.wirsRatio || 0) - Number(a.dataset.wirsRatio || 0));
          const active = new Set(candidates.slice(0, limit));

          gridVideos.forEach((video) => {
            if (active.has(video)) {
              ensureGridVideoReady(video);
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        };

        const setupObserver = () => {
          if (reduceMotion || !('IntersectionObserver' in window) || !gridVideos.length) return;

          observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              video.dataset.wirsRatio = entry.isIntersecting ? String(entry.intersectionRatio) : '0';

              if (entry.isIntersecting && !video.poster && video.dataset.poster) {
                video.poster = video.dataset.poster;
              }

              if (entry.isIntersecting) {
                warmProductImage(video.closest('[data-wirs-card]'));
              }
            });
            window.requestAnimationFrame(syncGridPlayback);
          }, {
            root: null,
            rootMargin: '80px 0px',
            threshold: [0, 0.25, 0.5, 0.75, 1]
          });

          gridVideos.forEach((video) => observer.observe(video));
        };

        const setMuteIcon = () => {
          const isMuted = lightboxVideo.muted;
          muteButton.classList.toggle('is-muted', isMuted);
          muteButton.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
          muteButton.setAttribute('aria-label', isMuted ? 'Turn sound on' : 'Mute video');
        };

        const hideToast = () => {
          toast?.classList.remove('is-on');
          if (toastTimer) {
            window.clearTimeout(toastTimer);
            toastTimer = 0;
          }
        };

        const showToast = (name) => {
          if (!toast) return;
          if (toastName) toastName.textContent = name || '';
          toast.classList.add('is-on');
          if (toastTimer) window.clearTimeout(toastTimer);
          toastTimer = window.setTimeout(hideToast, 3600);
        };

        const warmedProductImages = new Map();
        const warmedPeekPosters = new Map();
        const variantMapCache = new WeakMap();

        const getQuickCard = () => productSlot.querySelector('[data-wirs-quick-card]');

        const warmProductImage = (card) => {
          if (!card) return;

          const src = card.dataset.productImage || '';
          if (!src || warmedProductImages.has(src)) return;

          const image = new Image();
          image.decoding = 'async';
          image.src = src;
          warmedProductImages.set(src, image);
        };

        const canWarmPeekPosters = () => {
          const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
          if (!stageQuery?.matches || connection?.saveData) return false;
          return !['slow-2g', '2g'].includes(connection?.effectiveType || '');
        };

        const getWrappedIndex = (index) => {
          if (!cards.length) return 0;
          return (index + cards.length) % cards.length;
        };

        const warmPeekPoster = (index) => {
          if (!canWarmPeekPosters()) return;
          const card = getCard(getWrappedIndex(index));
          const src = card?.dataset.peekPoster || card?.dataset.poster || '';
          if (!src || warmedPeekPosters.has(src)) return;

          const image = new Image();
          image.decoding = 'async';
          image.src = src;
          warmedPeekPosters.set(src, image);
        };

        const warmAdjacentPosters = (index) => {
          if (cards.length < 2) return;
          warmPeekPoster(index - 1);
          warmPeekPoster(index + 1);
        };

        const setPeekPreview = (button, image, index, label) => {
          if (!button || !image) return;
          const card = getCard(getWrappedIndex(index));
          const src = card?.dataset.peekPoster || card?.dataset.poster || '';

          if (!card || !src) {
            button.hidden = true;
            image.removeAttribute('src');
            return;
          }

          button.dataset.index = String(getWrappedIndex(index));
          button.setAttribute('aria-label', `${label}: ${card.getAttribute('aria-label') || 'reel'}`);
          button.hidden = false;

          if (image.dataset.currentSrc !== src) {
            image.classList.add('is-changing');
            image.src = src;
            image.dataset.currentSrc = src;
            image.addEventListener('load', () => {
              window.requestAnimationFrame(() => image.classList.remove('is-changing'));
            }, { once: true });
          }
        };

        const syncPeekPreviews = (index = currentIndex) => {
          if (!peekPrev || !peekNext || !peekPrevImage || !peekNextImage) return;

          if (!stageQuery?.matches || cards.length < 2) {
            peekPrev.hidden = true;
            peekNext.hidden = true;
            return;
          }

          if (cards.length === 2) {
            peekPrev.hidden = true;
            setPeekPreview(peekNext, peekNextImage, index + 1, 'Next reel');
            return;
          }

          setPeekPreview(peekPrev, peekPrevImage, index - 1, 'Previous reel');
          setPeekPreview(peekNext, peekNextImage, index + 1, 'Next reel');
          warmAdjacentPosters(index);
        };

        const renderProductCard = (index) => {
          productSlot.textContent = '';

          const template = getTemplate('product', index);
          const fragment = cloneTemplate(template);
          if (!fragment) return;

          const card = getCard(index);
          const image = fragment.querySelector('[data-wirs-card-image]');

          if (image) {
            const src = image.dataset.src || card?.dataset.productImage || '';
            const fallback = card?.dataset.poster || '';

            if (src) {
              image.fetchPriority = 'high';
              image.src = src;

              image.addEventListener('error', () => {
                if (fallback && image.src !== fallback) image.src = fallback;
              }, { once: true });
            } else if (fallback) {
              image.src = fallback;
            }
          }

          productSlot.appendChild(fragment);
        };

        const renderVariantSeed = (index) => {
          variantSlot.textContent = '';
          const template = getTemplate('variant', index);
          const fragment = cloneTemplate(template);
          if (fragment) variantSlot.appendChild(fragment);
        };

        const loadLightboxMedia = (index) => {
          const card = getCard(index);
          if (!card) return;

          lightboxVideo.pause();
          lightboxVideo.removeAttribute('src');

          const poster = card.dataset.poster || '';
          const video = card.dataset.video || '';

          if (poster) lightboxVideo.poster = poster;
          else lightboxVideo.removeAttribute('poster');

          if (video) {
            lightboxVideo.src = video;
            lightboxVideo.load();

            if (!reduceMotion) {
              window.requestAnimationFrame(() => {
                lightboxVideo.play().catch(() => {});
              });
            }
          }
        };

        const closeVariantSheet = () => {
          if (variantController) {
            variantController.abort();
            variantController = null;
          }

          variantSheet.classList.remove('is-open');
          variantSheet.setAttribute('aria-hidden', 'true');
          variantBackdrop.hidden = true;
        };

        const openVariantSheet = () => {
          renderVariantSeed(currentIndex);

          const selector = variantSlot.querySelector('[data-wirs-variant-selector]');
          if (!selector) return;

          variantBackdrop.hidden = false;
          variantSheet.classList.add('is-open');
          variantSheet.setAttribute('aria-hidden', 'false');

          window.requestAnimationFrame(() => {
            selector.querySelector('[data-wirs-option], [data-wirs-variant-confirm]')?.focus();
          });
        };

        const loadReel = (requestedIndex) => {
          if (!cards.length) return;

          let index = requestedIndex;
          if (index < 0) index = cards.length - 1;
          if (index >= cards.length) index = 0;
          currentIndex = index;

          closeVariantSheet();

          renderProductCard(index);
          syncPeekPreviews(index);
          loadLightboxMedia(index);
        };

        const openLightbox = (index) => {
          lastFocus = document.activeElement;
          previousHtmlOverflow = document.documentElement.style.overflow;
          previousBodyOverflow = document.body.style.overflow;

          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';

          warmProductImage(getCard(index));
          warmAdjacentPosters(index);
          pauseGridVideos();

          lightbox.classList.add('is-open');
          lightbox.setAttribute('aria-hidden', 'false');

          lightboxVideo.muted = true;
          setMuteIcon();

          loadReel(index);
          window.requestAnimationFrame(() => closeButton.focus());
        };

        const closeLightbox = () => {
          if (!lightbox.classList.contains('is-open')) return;

          closeVariantSheet();
          lightbox.classList.remove('is-open');
          lightbox.setAttribute('aria-hidden', 'true');

          document.documentElement.style.overflow = previousHtmlOverflow;
          document.body.style.overflow = previousBodyOverflow;

          lightboxVideo.pause();
          lightboxVideo.removeAttribute('src');
          lightboxVideo.load();

          productSlot.textContent = '';
          variantSlot.textContent = '';
          if (peekPrev) peekPrev.hidden = true;
          if (peekNext) peekNext.hidden = true;

          syncGridPlayback();

          if (lastFocus && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
          }
        };

        const setQuickCardImage = (src) => {
          if (!src) return;

          const image = getQuickCard()?.querySelector('[data-wirs-card-image]');
          if (!image) return;

          if (image.src !== src) image.src = src;
        };

        const updateQuickCardFromSelector = (selector) => {
          const card = getQuickCard();
          if (!card || !selector) return;

          const title = selector.dataset.productTitle || '';
          const productLink = selector.dataset.productLink || selector.dataset.productUrl || '#';
          const image = selector.dataset.productImage || '';
          const price = selector.dataset.price || '';
          const compare = selector.dataset.compare || '';
          const discount = selector.dataset.discount || '';
          const variantId = selector.dataset.variantId || '';
          const available = selector.dataset.variantAvailable === 'true';

          card.dataset.productUrl = productLink;

          card.querySelectorAll('[data-wirs-card-title]').forEach((element) => {
            element.textContent = title;
            element.href = productLink;
          });

          card.querySelectorAll('[data-wirs-card-link], [data-wirs-card-view]').forEach((element) => {
            element.href = productLink;
          });

          if (image) setQuickCardImage(image);

          const priceElement = card.querySelector('[data-wirs-card-price]');
          if (priceElement) priceElement.textContent = price;

          const compareElement = card.querySelector('[data-wirs-card-compare]');
          if (compareElement) {
            compareElement.textContent = compare;
            compareElement.hidden = !compare;
          }

          const discountElement = card.querySelector('[data-wirs-card-discount]');
          if (discountElement) {
            discountElement.textContent = discount;
            discountElement.hidden = !discount;
          }

          const addButton = card.querySelector('[data-wirs-product-add]');
          if (addButton) {
            addButton.dataset.variantId = variantId;
            addButton.disabled = !available;
            addButton.textContent = available ? 'Add to cart' : 'Sold out';
          }
        };

        const getVariantMap = (selector) => {
          if (!selector || selector.dataset.localMap !== 'true') return null;
          if (variantMapCache.has(selector)) return variantMapCache.get(selector);

          const script = selector.querySelector('[data-wirs-variant-map]');
          if (!script) return null;

          try {
            const parsed = JSON.parse(script.textContent || '[]');
            const map = Array.isArray(parsed) ? parsed : [];
            variantMapCache.set(selector, map);
            return map;
          } catch (error) {
            console.warn('[WI Reel Shop] Local variant map failed to parse.', error);
            variantMapCache.set(selector, []);
            return [];
          }
        };

        const getOptionGroups = (selector) =>
          Array.from(selector.querySelectorAll('.wi-reel-shop__variant-option[data-option-position]'))
            .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

        const markOptionSelected = (button) => {
          const group = button.closest('.wi-reel-shop__variant-option');
          if (!group) return;

          group.querySelectorAll('[data-wirs-option]').forEach((chip) => {
            chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
          });
        };

        const selectedOptionValues = (selector) =>
          getOptionGroups(selector).map((group) => {
            const selected = group.querySelector('[data-wirs-option][aria-pressed="true"]');
            return selected?.dataset.optionValue || '';
          });

        const selectedOptionIds = (selector) =>
          getOptionGroups(selector)
            .map((group) => {
              const selected = group.querySelector('[data-wirs-option][aria-pressed="true"]');
              return selected?.dataset.optionValueId || '';
            })
            .filter(Boolean);

        const setSelectorStatus = (selector, message = '', type = '') => {
          const status = selector?.querySelector('[data-wirs-variant-status]');
          if (!status) return;

          status.textContent = message;
          status.hidden = !message;

          if (type) status.dataset.state = type;
          else status.removeAttribute('data-state');
        };

        const applyVariantData = (selector, variant) => {
          if (!selector || !variant) return;

          const available = Boolean(variant.available);
          const variantId = String(variant.id || '');
          const image = variant.image || '';

          selector.dataset.variantId = variantId;
          selector.dataset.variantAvailable = String(available);
          selector.dataset.price = variant.price || '';
          selector.dataset.compare = variant.compare || '';
          selector.dataset.discount = variant.discount || '';

          if (image) selector.dataset.productImage = image;

          const confirm = selector.querySelector('[data-wirs-variant-confirm]');
          if (confirm) {
            confirm.dataset.variantId = variantId;
            confirm.disabled = !available;
            confirm.textContent = available
              ? `Add to cart · ${variant.price || ''}`.trim()
              : 'Sold out';
          }

          setSelectorStatus(selector);
          updateQuickCardFromSelector(selector);
        };

        const updateLocalAvailability = (selector, variants) => {
          if (!selector || !variants?.length) return;

          const groups = getOptionGroups(selector);
          const selectedValues = selectedOptionValues(selector);

          groups.forEach((group) => {
            const position = Number.parseInt(group.dataset.optionPosition, 10) - 1;

            group.querySelectorAll('[data-wirs-option]').forEach((chip) => {
              const candidateValues = selectedValues.slice();
              candidateValues[position] = chip.dataset.optionValue || '';

              const isAvailable = variants.some((variant) =>
                variant.available &&
                Array.isArray(variant.options) &&
                variant.options.length === candidateValues.length &&
                variant.options.every((value, index) => value === candidateValues[index])
              );

              if (isAvailable) {
                chip.removeAttribute('data-unavailable');
                chip.disabled = false;
              } else {
                chip.dataset.unavailable = 'true';
                chip.disabled = true;
              }
            });
          });
        };

        const applyDirectVariantFromButton = (selector, button) => {
          const id = button.dataset.directVariantId || '';
          if (!id) return false;

          applyVariantData(selector, {
            id,
            available: button.dataset.directAvailable === 'true',
            price: button.dataset.directPrice || '',
            compare: button.dataset.directCompare || '',
            discount: button.dataset.directDiscount || '',
            image: button.dataset.directImage || ''
          });

          return true;
        };

        const selectLocalVariant = (selector, button) => {
          const variants = getVariantMap(selector);
          if (!variants?.length) return false;

          markOptionSelected(button);

          const selectedValues = selectedOptionValues(selector);
          const variant = variants.find((item) =>
            Array.isArray(item.options) &&
            item.options.length === selectedValues.length &&
            item.options.every((value, index) => value === selectedValues[index])
          );

          if (variant) applyVariantData(selector, variant);
          updateLocalAvailability(selector, variants);

          return true;
        };

        const fetchVariantSelector = async (selector, preferredProductUrl = '') => {
          if (!selector) return;

          const ids = selectedOptionIds(selector);
          if (!ids.length) return;

          const selectedButton = selector.querySelector('[data-wirs-option][aria-pressed="true"][data-product-url]');
          const requestedProductUrl = preferredProductUrl || selectedButton?.dataset.productUrl || selector.dataset.productUrl;
          if (!requestedProductUrl) return;

          const url = new URL(requestedProductUrl, window.location.origin);
          url.searchParams.set('section_id', 'wi-reel-shop-variant-panel');
          url.searchParams.set('option_values', ids.join(','));

          const cacheKey = url.toString();
          const controller = new AbortController();

          if (variantController) variantController.abort();
          variantController = controller;

          let slowStatusTimer = window.setTimeout(() => {
            if (selector.isConnected) {
              setSelectorStatus(selector, 'Checking availability…', 'loading');
            }
          }, 450);

          try {
            let html = variantCache.get(cacheKey);

            if (!html) {
              const response = await fetch(cacheKey, {
                headers: { Accept: 'text/html' },
                signal: controller.signal
              });

              if (!response.ok) {
                throw new Error(`Variant update failed (${response.status})`);
              }

              html = await response.text();
              variantCache.set(cacheKey, html);

              if (variantCache.size > 18) {
                variantCache.delete(variantCache.keys().next().value);
              }
            }

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const nextSelector = doc.querySelector('[data-wirs-variant-selector]');

            if (!nextSelector) {
              throw new Error('Variant selector missing from response.');
            }

            const imported = document.importNode(nextSelector, true);
            selector.replaceWith(imported);

            updateQuickCardFromSelector(imported);
          } catch (error) {
            if (error?.name === 'AbortError') return;

            console.warn('[WI Reel Shop] Variant update failed.', error);

            if (selector.isConnected) {
              setSelectorStatus(selector, 'Could not verify availability. Please try again.', 'error');
            }
          } finally {
            window.clearTimeout(slowStatusTimer);
            slowStatusTimer = 0;

            if (variantController === controller) {
              variantController = null;
            }
          }
        };

        const handleVariantOption = (button) => {
          const selector = button.closest('[data-wirs-variant-selector]');
          if (!selector || button.disabled) return;

          if (button.getAttribute('aria-pressed') === 'true') return;

          const currentProductUrl = selector.dataset.productUrl || '';
          const nextProductUrl = button.dataset.productUrl || currentProductUrl;
          const switchesProduct = Boolean(nextProductUrl && currentProductUrl && nextProductUrl !== currentProductUrl);

          if (!switchesProduct && selectLocalVariant(selector, button)) {
            return;
          }

          markOptionSelected(button);

          const optionCount = Number.parseInt(selector.dataset.optionCount || '0', 10);

          if (!switchesProduct && optionCount === 1 && applyDirectVariantFromButton(selector, button)) {
            return;
          }

          if (!switchesProduct) {
            applyDirectVariantFromButton(selector, button);
          }

          fetchVariantSelector(selector, nextProductUrl);
        };

        const setAddButtonState = (button, state, label = '') => {
          if (!button) return;

          button.classList.toggle('is-loading', state === 'loading');
          button.classList.toggle('is-added', state === 'added');

          if (state === 'loading') {
            button.setAttribute('aria-busy', 'true');
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-busy');
            button.removeAttribute('aria-disabled');
          }

          if (label) button.textContent = label;
        };

        const refreshCartDrawer = async (cartDrawer, variantId, focusTarget) => {
          if (
            !cartDrawer ||
            typeof cartDrawer.getSectionsToRender !== 'function' ||
            typeof cartDrawer.renderContents !== 'function'
          ) {
            return false;
          }

          const sectionIds = [...new Set(
            cartDrawer
              .getSectionsToRender()
              .map((item) => item.id || item.section)
              .filter(Boolean)
          )].slice(0, 5);

          if (!sectionIds.length) return false;

          if (cartRefreshController) cartRefreshController.abort();
          const controller = new AbortController();
          cartRefreshController = controller;
          const sequence = ++cartRefreshSequence;

          const refreshUrl = new URL(window.location.pathname, window.location.origin);
          refreshUrl.searchParams.set('sections', sectionIds.join(','));

          try {
            const response = await fetch(refreshUrl.toString(), {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              cache: 'no-store',
              signal: controller.signal,
              priority: 'low'
            });

            if (!response.ok) {
              throw new Error(`Cart drawer refresh failed (${response.status})`);
            }

            const sections = await response.json();
            if (sequence !== cartRefreshSequence || controller.signal.aborted) return false;
            if (!sections || !sections['cart-drawer']) return false;

            if (typeof cartDrawer.setActiveElement === 'function') {
              cartDrawer.setActiveElement(lastFocus || focusTarget);
            }

            hideToast();
            closeLightbox();
            cartDrawer.renderContents({
              id: Number(variantId),
              sections
            });

            return true;
          } catch (error) {
            if (error?.name !== 'AbortError') {
              console.warn('[WI Reel Shop] Cart drawer refresh failed.', error);
            }
            return false;
          } finally {
            if (cartRefreshController === controller) cartRefreshController = null;
          }
        };

        const addToCart = async (variantId, button) => {
          if (isAdding || !variantId) return;
          isAdding = true;

          const original = button?.textContent || 'Add to cart';
          const cartDrawer = document.querySelector('cart-drawer');
          const quickCard = getQuickCard();
          const productName = quickCard?.querySelector('[data-wirs-card-title]')?.textContent?.trim() || '';

          if (variantController) {
            variantController.abort();
            variantController = null;
          }
          lightboxVideo.pause();
          setAddButtonState(button, 'loading', 'Adding…');

          try {
            const response = await fetch(cartAddUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              credentials: 'same-origin',
              cache: 'no-store',
              priority: 'high',
              body: JSON.stringify({
                items: [{ id: Number(variantId), quantity: 1 }]
              })
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw result;

            setAddButtonState(button, 'added', 'Added ✓');
            closeVariantSheet();
            showToast(productName);
            isAdding = false;

            if (lightbox.classList.contains('is-open') && lightboxVideo.src) {
              lightboxVideo.play().catch(() => {});
            }

            if (cartDrawer) {
              void refreshCartDrawer(cartDrawer, variantId, button).then((opened) => {
                if (!opened && button?.isConnected) {
                  window.setTimeout(() => {
                    if (!button.isConnected) return;
                    button.classList.remove('is-added');
                    setAddButtonState(button, 'idle', original);
                  }, 900);
                }
              });
            } else if (button) {
              window.setTimeout(() => {
                if (!button.isConnected) return;
                button.classList.remove('is-added');
                setAddButtonState(button, 'idle', original);
              }, 1300);
            }
          } catch (error) {
            isAdding = false;
            console.warn('[WI Reel Shop] Add to cart failed.', error);

            if (lightbox.classList.contains('is-open') && lightboxVideo.src) {
              lightboxVideo.play().catch(() => {});
            }

            if (button) {
              button.classList.remove('is-added');
              setAddButtonState(button, 'idle', 'Try again');
              window.setTimeout(() => {
                if (button.isConnected) setAddButtonState(button, 'idle', original);
              }, 1600);
            }
          }
        };

        const handleRootClick = (event) => {
          const card = event.target.closest('[data-wirs-card]');
          if (card && root.contains(card)) {
            const index = Number.parseInt(card.dataset.index, 10) || 0;
            openLightbox(index);
            return;
          }

          if (event.target.closest('[data-wirs-close]')) {
            closeLightbox();
            return;
          }

          if (event.target.closest('[data-wirs-mute]')) {
            lightboxVideo.muted = !lightboxVideo.muted;
            setMuteIcon();
            return;
          }

          const peek = event.target.closest('[data-wirs-peek]');
          if (peek && lightbox.contains(peek)) {
            const index = Number.parseInt(peek.dataset.index, 10);
            if (Number.isFinite(index)) loadReel(index);
            return;
          }

          if (event.target.closest('[data-wirs-prev]')) {
            loadReel(currentIndex - 1);
            return;
          }

          if (event.target.closest('[data-wirs-next]')) {
            loadReel(currentIndex + 1);
            return;
          }

          const addButton = event.target.closest('[data-wirs-product-add]');
          if (addButton) {
            if (addButton.dataset.requiresOptions === 'true') openVariantSheet();
            else addToCart(addButton.dataset.variantId, addButton);
            return;
          }

          const optionButton = event.target.closest('[data-wirs-option]');
          if (optionButton && variantSlot.contains(optionButton)) {
            handleVariantOption(optionButton);
            return;
          }

          const confirmButton = event.target.closest('[data-wirs-variant-confirm]');
          if (confirmButton && variantSlot.contains(confirmButton)) {
            addToCart(confirmButton.dataset.variantId, confirmButton);
            return;
          }

          if (event.target.closest('[data-wirs-variant-close]')) {
            closeVariantSheet();
          }
        };

        const handleBackdropClick = (event) => {
          if (event.target === lightbox) closeLightbox();
          if (event.target === variantBackdrop) closeVariantSheet();
        };

        const handleKeydown = (event) => {
          if (!lightbox.classList.contains('is-open')) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            if (variantSheet.classList.contains('is-open')) closeVariantSheet();
            else closeLightbox();
            return;
          }

          if (!variantSheet.classList.contains('is-open')) {
            if (event.key === 'ArrowLeft' && prevButton) {
              event.preventDefault();
              loadReel(currentIndex - 1);
              return;
            }
            if (event.key === 'ArrowRight' && nextButton) {
              event.preventDefault();
              loadReel(currentIndex + 1);
              return;
            }
          }

          if (event.key !== 'Tab') return;

          const focusRoot = variantSheet.classList.contains('is-open') ? variantSheet : lightbox;
          const focusable = Array.from(focusRoot.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
            .filter((el) => el.offsetParent !== null);
          if (!focusable.length) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        };

        const handlePointerDown = (event) => {
          if (!lightbox.classList.contains('is-open') || variantSheet.classList.contains('is-open')) return;
          pointerStartX = event.clientX;
          pointerStartY = event.clientY;
        };

        const handlePointerUp = (event) => {
          if (!lightbox.classList.contains('is-open') || variantSheet.classList.contains('is-open')) return;
          const dx = event.clientX - pointerStartX;
          const dy = event.clientY - pointerStartY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
          loadReel(dx < 0 ? currentIndex + 1 : currentIndex - 1);
        };

        const handleWarmIntent = (event) => {
          const card = event.target.closest?.('[data-wirs-card]');
          if (card && root.contains(card)) {
            warmProductImage(card);
            const index = Number.parseInt(card.dataset.index, 10);
            if (Number.isFinite(index)) warmAdjacentPosters(index);
          }
        };

        const handleVisibility = () => syncGridPlayback();

        const handleReducedMotion = (event) => {
          reduceMotion = event.matches;
          if (reduceMotion) {
            observer?.disconnect();
            observer = null;
            pauseGridVideos();
          } else if (!observer) {
            setupObserver();
          }
        };

        root.addEventListener('click', handleRootClick);
        root.addEventListener('pointerover', handleWarmIntent, { passive: true });
        root.addEventListener('pointerdown', handleWarmIntent, { passive: true });
        lightbox.addEventListener('click', handleBackdropClick);
        viewer.addEventListener('pointerdown', handlePointerDown, { passive: true });
        viewer.addEventListener('pointerup', handlePointerUp, { passive: true });
        document.addEventListener('keydown', handleKeydown);
        document.addEventListener('visibilitychange', handleVisibility);

        if (mediaQuery) {
          if (typeof mediaQuery.addEventListener === 'function') mediaQuery.addEventListener('change', handleReducedMotion);
          else mediaQuery.addListener?.(handleReducedMotion);
        }

        const handleStageQueryChange = () => {
          if (lightbox.classList.contains('is-open')) syncPeekPreviews(currentIndex);
        };

        if (stageQuery) {
          if (typeof stageQuery.addEventListener === 'function') stageQuery.addEventListener('change', handleStageQueryChange);
          else stageQuery.addListener?.(handleStageQueryChange);
        }

        setupObserver();

        const warmInitialProductImages = () => {
          cards.slice(0, isMobile() ? 2 : 3).forEach(warmProductImage);
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warmInitialProductImages, { timeout: 1200 });
        } else {
          window.setTimeout(warmInitialProductImages, 300);
        }

        this.__wirsCleanup = () => {
          if (destroyed) return;
          destroyed = true;

          root.removeEventListener('click', handleRootClick);
          root.removeEventListener('pointerover', handleWarmIntent);
          root.removeEventListener('pointerdown', handleWarmIntent);
          lightbox.removeEventListener('click', handleBackdropClick);
          viewer.removeEventListener('pointerdown', handlePointerDown);
          viewer.removeEventListener('pointerup', handlePointerUp);
          document.removeEventListener('keydown', handleKeydown);
          document.removeEventListener('visibilitychange', handleVisibility);

          if (mediaQuery) {
            if (typeof mediaQuery.removeEventListener === 'function') mediaQuery.removeEventListener('change', handleReducedMotion);
            else mediaQuery.removeListener?.(handleReducedMotion);
          }

          if (stageQuery) {
            if (typeof stageQuery.removeEventListener === 'function') stageQuery.removeEventListener('change', handleStageQueryChange);
            else stageQuery.removeListener?.(handleStageQueryChange);
          }

          observer?.disconnect();
          observer = null;
          variantController?.abort();
          variantController = null;
          cartRefreshController?.abort();
          cartRefreshController = null;
          if (toastTimer) window.clearTimeout(toastTimer);
          pauseGridVideos();
          lightboxVideo.pause();

          if (lightbox.classList.contains('is-open')) {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
          }

          delete root.__wirsCleanup;
          root.dataset.wirsInitialized = 'false';
        };
      }

      disconnectedCallback() {
        if (typeof this.__wirsCleanup === 'function') {
          this.__wirsCleanup();
        }
      }
    });
  }
})();